// @flow
import React from 'react'
import * as THREE from 'three'

import { vec2 } from 'gl-matrix/dist/gl-matrix-min.js'

import type { ContextRouter } from 'react-router-dom'
import type ThreeApp from '../../utils/threeApp.js'
import type CameraApp from '../../utils/cameraApp.js'
import type DailyItemsApp from '../../utils/dailyItemsApp.js'
import type { DailyItemType } from 'database-helper'

type PropsType = {
  ...ContextRouter,
  dailyItem: DailyItemType,
  uid: string,
  index: number,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  ThreeApp: ThreeApp,
  DailyItemsApp: DailyItemsApp,
  lastArticleIndex: number,
  navbarClicked: boolean,
  CameraApp: CameraApp,
  texture: THREE.Texture,
  setPlaneCreated: (uid: string) => void
}

type StateType = {
  plane: ?THREE.PlaneGeometry,
  onScene: boolean
}

export default class Article extends React.Component<PropsType, StateType> {
  // shader values
  time: number
  // Animation values
  goal: vec2
  ratio: number

  constructor(props: PropsType) {
    super(props)
    this.time = 0
    this.ratio = 16/10
    this.state = {
      plane: undefined,
      onScene: false
    }
  }

  componentDidMount() {
    if (this.props.index === this.props.lastArticleIndex) {
      this.firstPlaneTextures(this.props.texture)
    } else if (this.props.index + 1 === this.props.lastArticleIndex) {
      const canvasTexture = this.props.ThreeApp.loadCanvasTexture(this.props.texture.image)
      this.createPlane(canvasTexture)
    } else {
      this.createPlane(this.props.texture)
    }
  }

  componentWillUnmount() {
    this.destroyPlane()
  }

  componentDidUpdate(prevProps: PropsType) {
    const plane = this.state.plane
    if (!plane) return null
    this.updateGoal(this.props.navbarClicked, plane) // maybe check if was before and now is after ?
    this.updatePlanePosition(plane)
    this.checkIfDisplayed(this.props.camera.position.z, plane.position.z)
  }

  updatePlanePosition = (plane: THREE.PlaneGeometry) => {
    const dir = vec2.fromValues(0, 0)
    const planeVec = vec2.fromValues(plane.position.y, plane.position.z)
    vec2.subtract(dir, this.goal, planeVec)
    vec2.scale(dir, dir, this.ratio / 50)
    vec2.add(planeVec, dir, planeVec)
    plane.position.set(plane.position.x, planeVec[0], planeVec[1])
  }

  updateGoal = (navbarView: boolean, plane: THREE.PlaneGeometry) => {
    const isBeforeCurrentArticle = this.props.index > this.props.DailyItemsApp.getDailyItemIndex()
    const zIndex = this.getZIndex(navbarView, isBeforeCurrentArticle)
    const yIndex = this.getYIndex(navbarView, isBeforeCurrentArticle)
    const posZ = this.props.ThreeApp.getUpdatedZPos(zIndex, this.props.DailyItemsApp.getOverviewItemIndex())
    const posY = this.props.ThreeApp.getUpdatedYPos(yIndex, this.props.DailyItemsApp.getOverviewItemIndex(), plane.geometry.parameters.height)
    this.goal = vec2.fromValues(posY, posZ)
  }

  getZIndex = (navbarView: boolean, isBeforeCurrentArticle: boolean): number => {
    if (!navbarView) return this.props.index
    if (isBeforeCurrentArticle) return this.props.index - 3
    return this.props.index  - 3
  }

  getYIndex = (navbarView: boolean, isBeforeCurrentArticle: boolean): number => {
    if (!navbarView) return this.props.index
    if (isBeforeCurrentArticle) return this.props.index + 2.5
    return this.props.index
  }

  firstPlaneTextures = (texture: string) => {
    this.props.ThreeApp.loadTextureAsync("/shaders/textures/formes.svg")
    .then(alphaTexture => {
      this.props.ThreeApp.loadTextureAsync("/shaders/textures/transparent.png")
      .then(blurTexture => {
        const colorObject = {...this.props.dailyItem.color}

        Object.keys(colorObject).forEach((key, index) => {
          colorObject[key] /= 255
        })

        let uniforms = {
          u_pic: { value: texture },
          u_mask: { value: alphaTexture },
          u_blur_pic: { value: blurTexture },
          u_color: { value: new THREE.Vector3(colorObject.r, colorObject.g, colorObject.b)},
          u_ratio: { value: 2.0 },
          u_time: { value: this.time },
          u_time_max: { value: 0. },
          u_movement: { value: .003 },
        };
        
        this.props.ThreeApp.setFirstArticleUniforms(uniforms)


        this.createShaderPlane(alphaTexture, texture)
      })
    
    })
    .catch(this.destroyPlane)
  }
  
  checkIfDisplayed = (cameraPosition: number, planePosition: number) => {
    const cameraIsBehind = cameraPosition < planePosition
    if (cameraIsBehind && this.state.onScene) this.destroyPlane()
    else if (!cameraIsBehind && !this.state.onScene) this.showPlane()
  }

  createPlane = (texture: THREE.Texture) => {
    const plane = this.props.ThreeApp.createArticlePlane(this.props.uid, this.props.index, texture)
    this.props.scene.add(plane)
    this.goal = vec2.fromValues(plane.position.z, plane.position.y)
    this.props.CameraApp.setPlaneHeight(plane.geometry.parameters.height)
    this.setState({ plane, onScene: true })
    this.props.setPlaneCreated(this.props.uid)
  }

  createShaderPlane = (alphaTexture: THREE.Texture, texture: THREE.Texture) => {
    this.props.ThreeApp.createShaderArticlePlane(this.props.uid, this.props.index, texture, alphaTexture, this.props.scene)
    .then((plane) => {
      this.props.scene.add(plane)
      this.goal = vec2.fromValues(plane.position.z, plane.position.y)
      this.setState({ plane, onScene: true })
      this.props.setPlaneCreated(this.props.uid)
    })
  }

  destroyPlane = () => {
    if (!this.state.plane) return undefined
    const plane = this.props.scene.getObjectByName(this.state.plane.name)
    this.props.scene.remove(plane)
    this.setState({ onScene: false })
  }

  showPlane = () => {
    const plane = this.state.plane
    if (!plane) return undefined
    this.props.scene.add(plane)
    this.setState({ onScene: true })
  }

  render() {
    return null
  }
}


// WEBPACK FOOTER //
// ./src/components/Article/index.jsx